/**
 * Instant collection filters (sections/collection-filters.liquid, snippets/filter-pill.liquid).
 *
 * Every pill, CLEAR FILTERS and the page links are real links to Shopify's filter URLs, so they
 * work without JS. Here a click on [data-filter-link] fetches the same URL through the Section
 * Rendering API for every section on the page that holds a [data-collection-section], swaps
 * those in, and pushes the URL. The server renders the bands again over the filtered products,
 * so colour Rule 1 is re-run by Liquid, never patched in JS.
 */
export const SECTION = '[data-collection-section]';

/** The filter URL with `sections=` asking for the given section ids. */
export function sectionsUrl(href, ids, base = globalThis.location?.href) {
  const url = new URL(href, base);
  url.searchParams.set('sections', ids.join(','));
  return url.toString();
}

const defaultParse = (html) => new DOMParser().parseFromString(html, 'text/html');

/**
 * Fetches `href` for every collection section, replaces each section's [data-collection-section]
 * and, with `history`, pushes `href`. Fires `shopify:section:load` per section so listeners
 * (the ?debug=colors checker, the list events) look again, then `collection:filtered` with the
 * { from, to } URLs for apply_filter. Throws when the request fails.
 */
export async function renderSections(
  href,
  { fetchImpl = globalThis.fetch, doc = globalThis.document, parse = defaultParse, history, signal, base } = {},
) {
  const sections = [...doc.querySelectorAll(SECTION)]
    .map((node) => ({ node, id: node.closest('.shopify-section')?.id.replace(/^shopify-section-/, '') }))
    .filter((section) => section.id);
  const response = await fetchImpl(sectionsUrl(href, sections.map((section) => section.id), base), { signal });
  if (!response.ok) throw new Error(`Section rendering failed (${response.status})`);
  const html = await response.json();
  const from = new URL(base ?? globalThis.location.href).href;

  history?.pushState({ collectionFilters: true }, '', href);
  for (const { node, id } of sections) {
    const fresh = html[id] && parse(html[id]).querySelector(SECTION);
    if (!fresh) continue;
    node.replaceWith(fresh);
    doc.dispatchEvent(new CustomEvent('shopify:section:load', { detail: { sectionId: id } }));
  }
  doc.dispatchEvent(new CustomEvent('collection:filtered', { detail: { from, to: new URL(href, from).href } }));
}

const doc = globalThis.document;
let controller;

async function navigate(href, { push = true } = {}) {
  controller?.abort();
  const mine = (controller = new AbortController());
  const focusKey = doc.activeElement?.closest?.('[data-focus-key]')?.dataset.focusKey;
  const busy = doc.querySelectorAll(SECTION);
  busy.forEach((node) => node.setAttribute('aria-busy', 'true'));
  try {
    await renderSections(href, { history: push ? globalThis.history : null, signal: mine.signal });
  } catch (error) {
    if (error.name === 'AbortError') return;
    globalThis.location.assign(href);
    return;
  } finally {
    if (controller === mine) busy.forEach((node) => node.removeAttribute('aria-busy'));
  }

  // Keep the keyboard where it was: the same pill after the swap. Otherwise (a page link) go to
  // the result count, scrolling up to it so the new page starts at its first band.
  const pill = focusKey && [...doc.querySelectorAll('[data-focus-key]')].find((node) => node.dataset.focusKey === focusKey);
  const count = doc.querySelector('[data-collection-count]');
  (pill || count)?.focus({ preventScroll: Boolean(pill) });
  const status = doc.querySelector('[data-collection-status]');
  if (status && count) status.textContent = count.textContent.trim();
}

if (doc?.querySelector) {
  doc.addEventListener('click', (event) => {
    const link = event.target.closest?.('a[data-filter-link]');
    if (!link || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    navigate(link.href);
  });
  globalThis.addEventListener('popstate', (event) => {
    if (event.state?.collectionFilters) navigate(globalThis.location.href, { push: false });
  });
  // The first entry needs a state too, so Back onto it re-renders instead of leaving it stale.
  globalThis.history.replaceState({ collectionFilters: true, ...globalThis.history.state }, '');
}
